import type React from "react"
import { Box, Typography, Skeleton, Grid, Container } from "@mui/material"
import { useRecordingHook } from "src/slices/recording/useRecordingHook"
import RecordingCard from "../RecordingCard/RecordingCard"
import { VideoLibrary } from "@mui/icons-material"

interface RecordingListProps {
  loading: boolean
}

export const RecordingList: React.FC<RecordingListProps> = ({ loading }) => {
  const { recordings } = useRecordingHook()

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          mb: 4,
          pb: 3,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <VideoLibrary sx={{ fontSize: 32, color: "#6a11cb" }} />
        <Typography variant="h4" component="h1" fontWeight="bold">
          Recordings
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ width: "100%" }}>
          {[1, 2, 3].map((item) => (
            <Box
              key={item}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                mb: 3,
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <Skeleton
                variant="rectangular"
                sx={{
                  width: { xs: "100%", sm: "240px" },
                  height: { xs: "180px", sm: "220px" },
                }}
                animation="wave"
              />
              <Box sx={{ p: 3, width: "100%" }}>
                <Skeleton variant="text" height={32} width="70%" sx={{ mb: 2 }} />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Skeleton variant="text" height={24} width="80%" sx={{ mb: 1 }} />
                    <Skeleton variant="text" height={24} width="60%" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Skeleton variant="text" height={24} width="40%" />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          ))}
        </Box>
      ) : recordings.length > 0 ? (
        <Box>
          {recordings.map((r) => (
            <RecordingCard key={r.sk.toString()} {...r} />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 3,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.8)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          }}
        >
          <VideoLibrary sx={{ fontSize: 60, color: "text.secondary", opacity: 0.5, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No recordings found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your recordings will appear here once they are available
          </Typography>
        </Box>
      )}
    </Container>
  )
}
